/**
 * WebSocket close codes (PLAN §5 "Close codes"), defined once for the gateway and every client.
 * The client's reaction to each code is `reconnectPolicy(code)`.
 */
export const CLOSE_CODES = Object.freeze({
  /** The client closes (sign-out, tab closed). */
  normal: 1000,
  /** The browser navigates away. */
  going_away: 1001,
  /** An inbound frame over 64 KiB. */
  frame_too_large: 1009,
  internal_error: 1011,
  /** The node is draining (deploy or scale-in); reconnect after `system.reconnect.afterMs`. */
  service_restart: 1012,
  /** Invalid JSON, a binary frame, an unknown op before auth, or an envelope that fails the schema. */
  bad_frame: 4000,
  /** No `auth` within 10 s. */
  auth_timeout: 4001,
  /** Bad signature, missing claims, or `auth.refresh` for another user or session. */
  auth_invalid: 4002,
  /** The grace period after `exp` passed. */
  token_expired: 4003,
  session_revoked: 4004,
  member_deactivated: 4005,
  /** The `v` in the URL is not supported. */
  protocol_unsupported: 4006,
  rate_limited: 4008,
  /** Backpressure limit exceeded. */
  slow_consumer: 4009,
  /** No client frame for 90 s. */
  heartbeat_timeout: 4010,
  /** This socket was the least recently active of more than 12. */
  too_many_connections: 4011,
} as const);

export type CloseCodeName = keyof typeof CLOSE_CODES;
export type CloseCode = (typeof CLOSE_CODES)[CloseCodeName];

const BY_CODE = new Map<number, CloseCodeName>(
  (Object.entries(CLOSE_CODES) as [CloseCodeName, number][]).map(([name, code]) => [code, name]),
);

export function closeCodeName(code: number): CloseCodeName | undefined {
  return BY_CODE.get(code);
}

/**
 * What a client does after a close:
 * - `backoff` — reconnect after `reconnectDelayMs(attempt)`;
 * - `refresh_then_reconnect` — refresh the access token over REST, then reconnect at once (4002, 4003);
 * - `after_server_delay` — wait `system.reconnect.afterMs` (default random 0–5 s), no backoff growth (1012);
 * - `delayed` — reconnect after at least `minDelayMs` (4008);
 * - `sign_out` — sign out locally, no reconnect (4004, 4005);
 * - `update_required` — show "Update required", no reconnect until reload (4006);
 * - `paused` — show "Paused: too many open tabs" with a Reconnect button (4011);
 * - `none` — the client closed on purpose (1000, 1001).
 */
export type ReconnectAction =
  | { kind: 'backoff' }
  | { kind: 'refresh_then_reconnect' }
  | { kind: 'after_server_delay' }
  | { kind: 'delayed'; minDelayMs: number }
  | { kind: 'sign_out' }
  | { kind: 'update_required' }
  | { kind: 'paused' }
  | { kind: 'none' };

export function reconnectPolicy(code: number): ReconnectAction {
  switch (code) {
    case CLOSE_CODES.normal:
    case CLOSE_CODES.going_away:
      return { kind: 'none' };
    case CLOSE_CODES.auth_invalid:
    case CLOSE_CODES.token_expired:
      return { kind: 'refresh_then_reconnect' };
    case CLOSE_CODES.service_restart:
      return { kind: 'after_server_delay' };
    case CLOSE_CODES.rate_limited:
      return { kind: 'delayed', minDelayMs: 10_000 };
    case CLOSE_CODES.session_revoked:
    case CLOSE_CODES.member_deactivated:
      return { kind: 'sign_out' };
    case CLOSE_CODES.protocol_unsupported:
      return { kind: 'update_required' };
    case CLOSE_CODES.too_many_connections:
      return { kind: 'paused' };
    default:
      return { kind: 'backoff' };
  }
}
